<template>
    <b-modal
        id="modal-add-item-to-group"
        ref="modal"
        :title="modalTitle"
        size="xl"
        static
        centered
        hide-footer
        @ok.prevent="handleSubmit()"
        @show="resetData()"
        @hidden="resetData()"
    >
        <DataTable
            :fields="fields"
            :items="items"
            :loading="requestSpaceGroups.getLoading()"
            :error="requestSpaceGroups.getError()"
            small
            no-padding
        >
            <template #headerSide>
                <ButtonIcon class="ml-auto" icon="plus" @click="showModalFormGroup()">{{ $t('addGroup') }}</ButtonIcon>
            </template>
            <template #cell(number)="{item}">
                <Tooltip :text="`${$t('categories')}: ${item.categoriesNo}\n${$t('products')}: ${item.productsNo}`" custom-class="pre align-items-start text-left">{{ item.productsNo + item.categoriesNo }}</Tooltip>
            </template>
            <template #cell(action)="{item}">
                <div class="d-flex gap-2 justify-content-end">
                    <ButtonIcon
                        v-if="!itemGroups.includes(item.id)"
                        v-b-tooltip.hover.noninteractive
                        :title="$t('addToGroup')"
                        size="sm"
                        icon="folder-plus"
                        variant="success"
                        :disabled="isGroupRemoving(item.id)"
                        :loading="requestGroupItems.getLoading(`add-${item.id}`) || requestGroups.getLoading(item.id) || isLoading"
                        @click="addItem(item)"
                    />
                    <ButtonIcon
                        v-if="itemGroups.includes(item.id)"
                        v-b-tooltip.hover.noninteractive
                        :title="$t('removeFromGroup')"
                        size="sm"
                        icon="folder-minus"
                        variant="danger"
                        :disabled="isGroupRemoving(item.id)"
                        :loading="requestGroupItems.getLoading(`remove-${item.id}`) || requestGroups.getLoading(item.id) || isLoading"
                        @click="removeItem(item)"
                    />
                    <ButtonIcon
                        size="sm"
                        icon="edit-2"
                        variant="primary"
                        :disabled="isGroupRemoving(item.id) || requestGroupItems.getLoading(`add-${item.id}`) || requestGroupItems.getLoading(`remove-${item.id}`) || requestGroups.getLoading(item.id) || isLoading"
                        @click="showModalFormGroup(item)"
                    />
                    <ButtonIcon
                        size="sm"
                        icon="trash-2"
                        variant="danger"
                        :disabled="isGroupRemoving(item.id) || requestGroupItems.getLoading(`add-${item.id}`) || requestGroupItems.getLoading(`remove-${item.id}`) || requestGroups.getLoading(item.id) || isLoading"
                        :loading="isGroupRemoving(item.id)"
                        @click="removeGroup(item)"
                    />
                </div>
            </template>
        </DataTable>
        <ModalFormGroup />
    </b-modal>
</template>



<script>
    import DataTable from '@/components/common/DataTable.vue';
    import Tooltip from '@/components/common/Tooltip.vue';
    import ModalFormGroup from './ModalFormGroup.vue';

    export default {
        name: 'ModalAddItemToGroup',
        components: {
            DataTable,
            Tooltip,
            ModalFormGroup,
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                requestGroups: this.$req('producerProductGroups/groups'),
                requestSpaceGroups: this.$req('producerProductGroups/spaceGroups'),
                requestGroupCategories: this.$req('producerProductGroups/groupCategories'),
                requestCategoryGroups: this.$req('producerProductCategory/groups'),
                requestGroupProducts: this.$req('producerProductGroups/groupProducts'),
                requestProductGroups: this.$req('producerProduct/groups'),
                requestMock: {
                    getLoading() {},
                    getData() {},
                },
                node: null,
            };
        },
        computed: {
            isCategory() {
                return !!this.node?.isBranch;
            },
            isProduct() {
                return !!this.node?.isLeaf;
            },
            itemType() {
                if (this.isCategory) return 'Category';
                if (this.isProduct) return 'Product';
                return '';
            },
            modalTitle() {
                if (this.isCategory) return this.$t('addCategoryToGroup');
                if (this.isProduct) return this.$t('addProductToGroup');
                return this.$t('addToGroup');
            },
            requestGroupItems() {
                if (this.isCategory) return this.requestGroupCategories;
                if (this.isProduct) return this.requestGroupProducts;
                return this.requestMock;
            },
            requestItemGroups() {
                if (this.isCategory) return this.requestCategoryGroups;
                if (this.isProduct) return this.requestProductGroups;
                return this.requestMock;
            },
            isLoading() {
                return this.requestItemGroups.getLoading(this.node?.id) || this.node?.childrenStates?.isLoading;
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            fields() {
                return [
                    { key: 'id', label: this.$t('id') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'description', label: this.$t('description') },
                    { key: 'number', label: this.$t('contains') },
                    { key: 'action', label: '', tdClass: 'text-right', thClass: 'text-right' },
                ];
            },
            items() {
                return this.requestSpaceGroups.getData()?.items || [];
            },
            itemGroups() {
                if (!this.node?.id) return [];
                return this.requestItemGroups.getData(this.node.id)?.items?.map(item => item.id) || [];
            },
        },
        methods: {
            onInit(node) {
                this.node = node;
                this.fetchItemGroups();
            },
            fetchGroup(id) {
                return this.requestGroups
                    .id(id)
                    .get({ urlParams: { id } })
                    .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
            },
            fetchSpaceGroups() {
                return this.requestSpaceGroups.get({ urlParams: { spaceId: this.spaceId }, requestParams: { page: 1, perPage: Number.MAX_SAFE_INTEGER, sort: 'id', order: 'DESC' } });
            },
            fetchItemGroups() {
                return this.requestItemGroups.id(this.node.id).get({ urlParams: { id: this.node.id } });
            },
            fetchData() {
                this.fetchSpaceGroups();
                this.fetchItemGroups();
            },
            async checkGroup(groupId) {
                let result = false;
                if (this.isCategory) result = await this.checkCategoryGroup(groupId);
                if (this.isProduct) result = await this.checkProductGroup(groupId);
                return result;
            },
            async checkCategoryGroup(groupId) {
                const group = await this.fetchGroup(groupId);
                const disabledNodes = [...group.products, ...group.categories].map(item => item.id);
                const childIds = this.node.children?.map(item => item.id) || [];
                if (childIds.some(id => disabledNodes.includes(id))) {
                    this.$toastr.warning(this.$t(`add${this.itemType}ChildExistError`, { name: this.node.label }));
                    return false;
                }
                return true;
            },
            async checkProductGroup(groupId) {
                const group = await this.fetchGroup(groupId);
                const disabledNodes = [...group.products, ...group.categories].map(item => item.id);
                if (this.node.parentNode?.id && disabledNodes.includes(this.node.parentNode.id)) {
                    this.$toastr.warning(this.$t(`addProduct${this.itemType}ExistError`, { name: this.node.label }));
                    return false;
                }
                return true;
            },
            async addItem(item) {
                const isOK = await this.checkGroup(item.id);
                if (!isOK) return false;
                return this.requestGroupItems
                    .id(`add-${item.id}`)
                    .post({ urlParams: { groupId: item.id, id: this.node.id } })
                    .then(() => {
                        this.fetchData();
                        this.$toastr.success(this.$t(`added${this.itemType}ToGroup`));
                    })
                    .catch(error => {
                        if (error.errorCode === 'ALREADY_ADDED') {
                            this.fetchData();
                        } else {
                            this.$toastr.error(error.message, this.$t('errorOccurred'));
                        }
                    });
            },
            removeItem(item) {
                return this.requestGroupItems
                    .id(`remove-${item.id}`)
                    .delete({ urlParams: { groupId: item.id, id: this.node.id } })
                    .then(() => {
                        this.fetchData();
                        this.$toastr.success(this.$t(`removed${this.itemType}FromGroup`));
                    })
                    .catch(error => {
                        if (error.errorCode === 'NOT_RELATED') {
                            this.fetchData();
                        } else {
                            this.$toastr.error(error.message, this.$t('errorOccurred'));
                        }
                    });
            },
            resetData() {
                this.node = null;
            },
            isGroupRemoving(id) {
                return this.requestGroups.getLoading(`delete-${id}`);
            },
            removeGroup(item) {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmYouWantDelete'), {
                        title: this.$t('confirm'),
                        okTitle: this.$t('yes'),
                        cancelTitle: this.$t('cancel'),
                        okVariant: 'danger',
                    })
                    .then(value => {
                        if (value) {
                            this.requestGroups
                                .id(`delete-${item.id}`)
                                .delete({ urlParams: { id: item.id } })
                                .then(this.fetchData)
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            showModalFormGroup(item) {
                this.$root.$emit('bv::show::modal', 'modal-form-group');
                this.$root.$emit('modal-form-group', item);
            },
        },
        created() {
            this.fetchSpaceGroups();
        },
        mounted() {
            this.$root.$on('modal-add-item-to-group', this.onInit);
        },
        beforeDestroy() {
            this.$root.$off('modal-add-item-to-group', this.onInit);
        },
    };
</script>
